import React, { Fragment, useCallback, useEffect, useState } from "react";
import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import { EkasiServeAppBar, equalObj, LoadingPage, selectAllOnFocus, SetState } from "./Common";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Grid from "@mui/material/Grid";
import { Link, useNavigate, useParams } from "react-router-dom";
import TextField from "@mui/material/TextField";
import Snackbar from "@mui/material/Snackbar";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import { Container, List, ListItem, ListItemButton, Popover } from "@mui/material";
import { Add, Edit } from "@mui/icons-material";

export interface Supplier {
    id: number;
    name: string;
}

function ProductsPopover({ supplier }: { supplier: Supplier }) {
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    return (
        <Fragment>
            <Button aria-describedby="Products" onClick={(ev) => setAnchorEl(ev.currentTarget)} variant="contained" fullWidth>
                Products
            </Button>
            <Popover
                id="Products"
                open={anchorEl !== null}
                onClose={() => setAnchorEl(null)}
                anchorEl={anchorEl}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>

                <List>
                    <ListItem>
                        <ListItemButton component={Link} to={"/suppliers/" + supplier.id + "/products"}> Show Products </ListItemButton>
                    </ListItem>
                    <ListItem>
                        <ListItemButton component={Link} to={"/suppliers/" + supplier.id + "/products/0"}> Create Product </ListItemButton>
                    </ListItem>
                </List>
            </Popover>
        </Fragment>
    )
}

export function SuppliersTable({ suppliers }: { suppliers: Array<Supplier> }) {
    return (
        <Grid container spacing={2} sx={{ alignItems: "center" }}>
            <Grid size={12}>
                <Paper>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell width="80%">Name</TableCell>
                                    <TableCell width="15%"> </TableCell>
                                    <TableCell width="5%">
                                        <Button variant="contained" component={Link} to={"/suppliers/0"} fullWidth>
                                            <Add />
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {suppliers.map((row) => (
                                    <TableRow key={row.id}>
                                        <TableCell>{row.name}</TableCell>
                                        <TableCell>
                                            <ProductsPopover supplier={row} />
                                        </TableCell>
                                        <TableCell>
                                            <Button variant="contained" component={Link} to={"/suppliers/" + row.id} fullWidth>
                                                <Edit />
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>

            </Grid>
        </Grid>
    )
}

export const SuppliersPage = () => {
    const [suppliers, setSuppliers] = useState<Array<Supplier>>([]);
    const [snackbar, setSnackbar] = useState("");

    useEffect(() => {
        const fetchSuppliers = async () => {
            const response = await fetch("/api/v1/suppliers");
            const data = await response.json();
            setSuppliers(data.result || []);
            if (data.error) {
                setSnackbar(data.error);
            }
        };
        fetchSuppliers();
    }, []);

    return (
        <Box>
            <EkasiServeAppBar />
            <Grid component={Container} container spacing={2} sx={{ alignItems: "center" }}>
                <Grid size={12}> <h1> Suppliers </h1> </Grid>
                <Grid size={12}>
                    <SuppliersTable suppliers={suppliers}/>
                </Grid>
            </Grid>
            <Snackbar
                open={snackbar !== ""}
                autoHideDuration={6000}
                onClose={(ev, reason) => setSnackbar("")}
                message={snackbar}/>
        </Box>
    )
}

export function SupplierForm({ supplier, setSupplier }: { supplier: Partial<Supplier>, setSupplier: SetState<Partial<Supplier>> }) {
    return (
        <Grid container spacing={2} sx={{ alignItems: "center" }}>
            <Grid size={1}></Grid>
            <Grid size={10}>
                <TextField
                    id="name"
                    label="Name"
                    variant="outlined"
                    onFocus={selectAllOnFocus}
                    onChange={(e) => setSupplier(supplier => ({ ...supplier, name: e.target.value }))}
                    fullWidth
                    value={supplier.name || ""}
                />
            </Grid>
            <Grid size={1}></Grid>
        </Grid>
    )
}

export const SupplierPage = () => {
    const { supplierIdText } = useParams();
    const [supplier, setSupplier] = useState<Partial<Supplier>>({});
    const [snackbar, setSnackbar] = useState("");
    const [requestDelete, setRequestDelete] = useState(false);
    const [edited, setEdited] = useState(false);
    const [savedSupplier, setSavedSupplier] = useState<Partial<Supplier>>({});
    const [isLoading, setIsLoading] = useState(0);
    const navigate = useNavigate();

    const supplierId = parseInt(supplierIdText || "0");

    useEffect(() => {
        const fetchSupplier = async () => {
            const response = await fetch("/api/v1/suppliers/" + supplierId);
            const data = await response.json();
            setSupplier(data.result || {});
            setSavedSupplier(data.result || {});
            if(data.error) {
                setSnackbar(data.error);
            }
            setIsLoading(lock => lock - 1);
        }
        setIsLoading(lock => lock + 1);
        fetchSupplier();
    }, [supplierId]);

    useEffect(() => {
        setEdited(!equalObj(supplier, savedSupplier));
    }, [supplier, savedSupplier]);

    const saveSupplier = useCallback(async () => {
        const response = await fetch("/api/v1/suppliers", {
            method: supplier.id === 0 ? "POST" : "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(supplier)
        });
        const data = await response.json();
        setSupplier(data.result || {});
        setSavedSupplier(data.result || {});
        if (data.error) {
            setSnackbar(data.error);
        } else {
            if (supplierId === 0) {
                navigate("/suppliers/" + data.result.id, { replace: true });
            }
            setSnackbar("Information Saved");
        }
    }, [supplier, supplierId, navigate]);

    const deleteSupplier = useCallback(async () => {
        const response = await fetch("/api/v1/suppliers/" + supplier.id, { method: "DELETE" });
        const data = await response.json();
        if (data.error) {
            setSnackbar(data.error);
        } else {
            setSupplier(data.result || {});
            navigate(-1);
        }
    }, [navigate, supplier.id]);

    if (isLoading) return (<LoadingPage />);

    return (
        <Stack spacing={0}>
            <EkasiServeAppBar />
            <Grid component={Container} container spacing={2} sx={{ alignItems: "center" }}>
                <Grid size={10}>
                    <h1>{supplier.name || "New Supplier"}</h1>
                </Grid>
                <Grid size={1}>
                    {
                        ((supplier.id || 0) === 0)
                            ? <Button variant="contained" onClick={() => navigate(-1)} fullWidth> Cancel </Button>
                            : <Button variant="contained" onClick={() => setRequestDelete(true)} color="error" fullWidth> Delete </Button>
                    }
                </Grid>
                <Grid size={1}>
                    <Button disabled={!edited} variant="contained" onClick={saveSupplier}> Save </Button>
                </Grid>
                <Grid size={12}>
                    <SupplierForm supplier={supplier} setSupplier={setSupplier} />
                </Grid>
            </Grid>

            <Snackbar
                open={snackbar !== ""}
                autoHideDuration={6000}
                onClose={(ev, reason) => setSnackbar("")}
                message={snackbar}/>
            <Dialog open={requestDelete}>
                <DialogTitle> Are you sure you want to delete this supplier? </DialogTitle>
                <Button onClick={deleteSupplier}> Yes, Delete </Button>
                <Button onClick={() => setRequestDelete(false)}> No, Cancel </Button>
            </Dialog>
        </Stack>

    )
}